import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import SupplierRebateRuleForm from './SupplierRebateRuleForm';
import RowActionsMenu from '../../../shared/RowActionsMenu';
import { ActiveStatusPill } from '../../../shared/Pill';
import { PAGE_SIZE_OPTIONS, FALLBACK_PAGE_SIZE, useDefaultPageSize, getCachedDefaultPageSize, Pagination } from '../../../shared/pagination';
import { useErrorToast, useDismissError } from '../../../shared/ErrorToastContext';
import '../../../shared/ListRowHover.css';
import '../../../shared/StyledInput.css';
import { formatDate } from '../../../shared/tenantClock';
import { formatPercent } from '../../../shared/numberFormat';
import { useScreenAccess } from '../../../shared/PermissionsContext';
import { askConfirm } from '../../../shared/AppDialogs';

import CancelButton from '../../../shared/CancelButton';
function formatDateRange(item) {
  if (!item.start_date && !item.end_date) return '—';
  return `${formatDate(item.start_date) || '…'} – ${formatDate(item.end_date) || '…'}`;
}

// The Generate action's own inline panel — preview-then-confirm, since
// bulk-creating/deactivating potentially many SKU Rate rows in one action
// deserves a look-before-you-leap step (see vis_supplier_rebates.md).
function GeneratePanel({ item, onClose, onDone }) {
  const showError = useErrorToast();
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    axios.get(`/api/supplier-rebate-rules/${item.id}/generate/preview`)
      .then((res) => setPreview(res.data.data))
      .catch((err) => showError(err, 'Preview failed'))
      .finally(() => setLoading(false));
    // eslint-disable-next-line
  }, [item.id]);

  const handleConfirm = async () => {
    setRunning(true);
    try {
      const res = await axios.post(`/api/supplier-rebate-rules/${item.id}/generate`);
      setResult(res.data);
      onDone();
    } catch (err) {
      showError(err, 'Generate failed');
    }
    setRunning(false);
  };

  return (
    <div style={{ background: '#f7f9fc', border: '1px solid #e3e7ee', padding: 20, margin: '12px 24px 0', borderRadius: 8 }}>
      <div style={{ fontWeight: 600, marginBottom: 8 }}>Generate SKU Rates — {item.name}</div>

      {loading && <div style={{ color: '#666', fontSize: 14 }}>Working out what's in scope…</div>}

      {!loading && !result && preview && (
        <>
          <ul style={{ margin: '0 0 14px', paddingLeft: 20, fontSize: 14 }}>
            <li>Will create <strong>{preview.to_create}</strong> new SKU Rate row{preview.to_create === 1 ? '' : 's'}</li>
            <li>Will deactivate <strong>{preview.to_deactivate}</strong> existing row{preview.to_deactivate === 1 ? '' : 's'} that left scope</li>
            {preview.skipped > 0 && (
              <li>Will skip <strong>{preview.skipped}</strong> in-scope item{preview.skipped === 1 ? '' : 's'} that can't be calculated (missing UoM or price) — <Link to="/purchasing-reports" style={{ color: '#1976d2', textDecoration: 'underline' }}>run Integrity Report</Link> to investigate</li>
            )}
          </ul>
          <button type="button" onClick={handleConfirm} disabled={running} style={{ padding: '8px 18px', background: '#1976d2', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 500, cursor: running ? 'not-allowed' : 'pointer', opacity: running ? 0.6 : 1 }}>
            {running ? 'Generating…' : 'Confirm'}
          </button>
          <CancelButton onClick={onClose} disabled={running} style={{ marginLeft: 8, padding: '8px 18px' }} />
        </>
      )}

      {result && (
        <>
          <div style={{ fontSize: 14, marginBottom: 14 }}>
            {result.message}
            {result.data?.skipped > 0 && (
              <>
                {' '}
                <Link to="/purchasing-reports" style={{ color: '#1976d2', textDecoration: 'underline' }}>Open Integrity Report</Link>
              </>
            )}
          </div>
          <button type="button" onClick={onClose} style={{ padding: '8px 18px' }}>Close</button>
        </>
      )}
    </div>
  );
}

function SupplierRebateRuleTable() {
  const { canCreate, canUpdate } = useScreenAccess();
  const showError = useErrorToast();
  const dismissError = useDismissError();
  const [showForm, setShowForm] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [duplicateFrom, setDuplicateFrom] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [generatingItem, setGeneratingItem] = useState(null);
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0, pages: 1 });
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(() => getCachedDefaultPageSize() || FALLBACK_PAGE_SIZE);
  const [status, setStatus] = useState('active');
  const [loading, setLoading] = useState(false);

  useDefaultPageSize(setLimit);

  useEffect(() => {
    fetchRows();
    // eslint-disable-next-line
  }, [status, page, limit]);

  const fetchRows = async () => {
    setLoading(true);
    try {
      const res = await axios.get('/api/supplier-rebate-rules', { params: { status, page, limit } });
      setRows(res.data.data || []);
      setPagination(res.data.pagination || { page, limit, total: 0, pages: 1 });
    } catch (err) {
      setRows([]);
      setPagination({ page, limit, total: 0, pages: 1 });
    }
    setLoading(false);
  };

  const handleAdd = () => {
    setEditItem(null);
    setDuplicateFrom(null);
    setShowForm(true);
  };

  const handleEdit = (item) => {
    setEditItem(item);
    setDuplicateFrom(null);
    setShowForm(true);
  };

  // Pre-fills the Add form from an existing Rule (minus id/name) so staff
  // can adjust dates/rate for a new period without losing the old Rule as
  // history — the lightweight stand-in for real Rule versioning.
  const handleDuplicate = (item) => {
    setEditItem(null);
    setDuplicateFrom({ ...item, name: `${item.name} (copy)` });
    setShowForm(true);
  };

  const handleDeactivate = async (item) => {
    if (!(await askConfirm(`Deactivate Supplier Rebate Rule "${item.name}"?`, { confirmLabel: 'Deactivate' }))) return;
    try {
      await axios.patch(`/api/supplier-rebate-rules/${item.id}/deactivate`);
      fetchRows();
      dismissError();
    } catch (err) {
      showError(err, 'Deactivate failed');
    }
  };

  const handleReactivate = async (item) => {
    try {
      await axios.patch(`/api/supplier-rebate-rules/${item.id}/reactivate`);
      fetchRows();
      dismissError();
    } catch (err) {
      showError(err, 'Reactivate failed');
    }
  };

  const handleSave = async (data) => {
    setFormLoading(true);
    try {
      if (editItem) {
        await axios.put(`/api/supplier-rebate-rules/${editItem.id}`, data);
      } else {
        await axios.post('/api/supplier-rebate-rules', data);
      }
      setShowForm(false);
      setDuplicateFrom(null);
      fetchRows();
      dismissError();
    } catch (err) {
      showError(err, 'Save failed');
    }
    setFormLoading(false);
  };

  return (
    <div style={{ margin: '32px 24px 48px', background: '#fafafa', border: '1px solid #eceef1', borderRadius: 12, boxShadow: '0 1px 6px rgba(0,0,0,0.04)', paddingBottom: 32 }}>
      <h2 style={{ padding: '20px 24px 0', margin: 0, fontWeight: 600, fontSize: 20 }}>Supplier Rebate Rules</h2>
      <div style={{ padding: '4px 24px 0', color: '#666', fontSize: 13 }}>
        A Supplier's rebate deal for a Brand + Category — use Generate to bulk-create SKU Rates for everything in scope.
      </div>
      <div style={{ display: 'flex', gap: 10, margin: '16px 24px 0', alignItems: 'center', flexWrap: 'wrap' }}>
        {canCreate && (
          <button
            onClick={handleAdd}
            disabled={showForm}
            title={showForm ? 'Finish editing first' : undefined}
            style={{ padding: '8px 18px', background: '#1976d2', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 500, cursor: showForm ? 'not-allowed' : 'pointer', opacity: showForm ? 0.6 : 1 }}
          >
            Add Rule
          </button>
        )}
        <select className="styled-input" value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }} style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #bfc8d6', fontSize: 14 }}>
          <option value="active">Active items</option>
          <option value="inactive">Deactivated items</option>
        </select>
      </div>

      {showForm && (
        <div style={{ background: '#f7f9fc', border: '1px solid #e3e7ee', padding: 20, margin: '18px 24px 0', borderRadius: 8 }}>
          <SupplierRebateRuleForm
            key={editItem?.id ?? (duplicateFrom ? `duplicate-${duplicateFrom.id}` : 'add')}
            initial={editItem || duplicateFrom}
            onSave={handleSave}
            onCancel={() => { setShowForm(false); setDuplicateFrom(null); dismissError(); }}
            loading={formLoading}
          />
        </div>
      )}

      {generatingItem && (
        <GeneratePanel
          item={generatingItem}
          onClose={() => setGeneratingItem(null)}
          onDone={fetchRows}
        />
      )}

      <div style={{ margin: '24px 24px 0', background: '#fff', border: '1px solid #b3bbc7', borderRadius: 10, overflow: 'visible', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 16, tableLayout: 'fixed' }}>
          <colgroup>
            <col style={{ width: '18%' }} />
            <col style={{ width: '10%' }} />
            <col style={{ width: '9%' }} />
            <col style={{ width: '13%' }} />
            <col style={{ width: '11%' }} />
            <col style={{ width: '11%' }} />
            <col style={{ width: '17%' }} />
            <col style={{ width: '6%' }} />
            <col style={{ width: '5%' }} />
          </colgroup>
          <thead className="list-head" style={{ fontSize: 14 }}>
            <tr>
              <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'left' }}>Name</th>
              <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'left' }}>Supplier</th>
              <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'left' }}>Brand</th>
              <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'left' }}>Category</th>
              <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'left' }}>UoM1 → UoM2</th>
              <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'right' }}>Rate</th>
              <th style={{ padding: '12px 8px 12px 24px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'left' }}>Dates</th>
              <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'center' }}>Status</th>
              <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'center' }}></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={9} style={{ padding: '14px', textAlign: 'center', background: '#f6f8fa', fontSize: 14 }}>Loading...</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={9} style={{ padding: '14px', textAlign: 'center', background: '#f6f8fa', fontSize: 14 }}>No results</td></tr>
            ) : rows.map((item) => (
              <tr
                key={item.id}
                className="list-row"
                onClick={() => { if (!showForm && canUpdate) handleEdit(item); }}
                style={{ cursor: showForm || !canUpdate ? 'default' : 'pointer' }}
                title={showForm || !canUpdate ? undefined : 'Click to edit'}
              >
                <td style={{ padding: '6px 8px', fontSize: 14, fontWeight: 600 }} title={item.description || ''}>{item.name}</td>
                <td style={{ padding: '6px 8px', fontSize: 14 }}>{item.supplier?.name || '—'}</td>
                <td style={{ padding: '6px 8px', fontSize: 14 }}>{item.brand?.name || ''}</td>
                <td style={{ padding: '6px 8px', fontSize: 13 }}>{item.product_category?.name || '—'}</td>
                <td style={{ padding: '6px 8px', fontSize: 14 }}>{item.uom1?.name} → {item.uom2?.name}</td>
                <td style={{ padding: '6px 8px', fontSize: 14, textAlign: 'right' }}>{formatPercent(item.rebate_rate, 1)}</td>
                <td style={{ padding: '6px 8px', fontSize: 14 }}>{formatDateRange(item)}</td>
                <td style={{ padding: '6px 8px', textAlign: 'center', fontSize: 13 }}>
                  <ActiveStatusPill active={item.is_active} />
                </td>
                <td style={{ padding: '6px 8px', textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                  <RowActionsMenu
                    items={[
                      { key: 'generate', label: 'Generate', disabled: showForm || !item.is_active, disabledTitle: !item.is_active ? 'Reactivate this Rule first' : (showForm ? 'Finish editing first' : undefined), onClick: () => setGeneratingItem(item) },
                      { key: 'edit', label: 'Edit', disabled: showForm, disabledTitle: showForm ? 'Finish editing first' : undefined, onClick: () => handleEdit(item) },
                      { key: 'duplicate', label: 'Duplicate', disabled: showForm, disabledTitle: showForm ? 'Finish editing first' : undefined, onClick: () => handleDuplicate(item) },
                      item.is_active
                        ? { key: 'deactivate', label: 'Deactivate', danger: true, disabled: showForm, disabledTitle: showForm ? 'Finish editing first' : undefined, onClick: () => handleDeactivate(item) }
                        : { key: 'reactivate', label: 'Reactivate', disabled: showForm, disabledTitle: showForm ? 'Finish editing first' : undefined, onClick: () => handleReactivate(item) },
                    ]}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Pagination
        pagination={pagination}
        setPage={setPage}
        totalLabel="Supplier Rebate Rules"
        rightContent={(
          <select className="styled-input"
            value={limit}
            onChange={(e) => { setLimit(Number(e.target.value)); setPage(1); }}
            style={{ marginLeft: 8, padding: '2px 4px', border: 'none', background: 'none', fontSize: 14, fontWeight: 600, color: '#333', cursor: 'pointer' }}
          >
            {PAGE_SIZE_OPTIONS.map((sz) => <option key={sz} value={sz}>{sz} per page</option>)}
          </select>
        )}
      />
    </div>
  );
}

export default SupplierRebateRuleTable;
